import React from "react";
import useCourses from "../../lib/hooks/use-courses";
import airtable from "../../lib/airtables";
import { airtableFields } from "../../lib/airtables/schema";
import "./accordion.css";

interface AccordionItemProps {
  title: string;
  children: React.ReactNode;
}

const AccordionItem: React.FC<AccordionItemProps> = React.memo(({ title, children }) => {
	const [isOpen, setIsOpen] = React.useState(false);
	const toggleIsOpen = React.useCallback(() => {
		setIsOpen(prevIsOpen => !prevIsOpen);
	  }, []);
  
	return (
	  <div className="accordion-item">
		<div className="accordion-title" onClick={toggleIsOpen}>
		  {title}
		</div>
		{isOpen && <div className="accordion-content">{children}</div>}
	  </div>
	);
});

export default function CoursesPage() {
	const { user, courses, units, weeks, lessons, resources } = useCourses();

	const getUnitsForCourse = React.useCallback((courseId: string) => {
		return units
			.filter(unit => 
				Array.isArray(unit?.[airtableFields.units.course]) && unit[airtableFields.units.course].includes(courseId)
			)
			.sort((a, b) => a[airtableFields.units.unit].localeCompare(b[airtableFields.units.unit]));
	}, [units]);

	const getWeeksForUnit = React.useCallback((unitId: string) => {
		return weeks
		  .filter(week => 
			Array.isArray(week?.[airtableFields.weeks.unit]) && week[airtableFields.weeks.unit].includes(unitId)
		  )
			.sort((a, b) => a[airtableFields.weeks.week].localeCompare(b[airtableFields.weeks.week]));
	}, [weeks]);
  
	const getLessonsForWeek = React.useCallback((weekId: string) => {
	  return lessons
		.filter(lesson => 
		Array.isArray(lesson?.[airtableFields.lessons.weeks]) && lesson[airtableFields.lessons.weeks].includes(weekId)
		)
		.sort((a, b) => a[airtableFields.lessons.lesson].localeCompare(b[airtableFields.lessons.lesson]));
	}, [lessons]);
	
	const getResourcesForLesson = React.useCallback((lessonId: string) => {
	  return resources
		.filter(resource => 
		Array.isArray(resource?.[airtableFields.resources.lessons]) && resource[airtableFields.resources.lessons].includes(lessonId)
		)
		.sort((a, b) => a[airtableFields.resources.name].localeCompare(b[airtableFields.resources.name]));
	}, [resources]);

  // Define the clickHandler function
	const clickHandler = React.useCallback((table: "course" | "unit" | "week" | "resource", field: string, recordId: string, oldUsers: Array<string>, userId: string) => {
	// Define the updatedUsers variable
	let updatedUsers;
	// Check if oldUsers is an array and has data
	if (Array.isArray(oldUsers) && oldUsers.length > 0) {
	  // Add userId to the oldUsers array and remove duplicates
	  updatedUsers = Array.from(new Set([...oldUsers, userId]));
	} else {
	  updatedUsers = [userId];
	}

	// Update the record in Airtable
	let update_details = {
		[field]: updatedUsers
	};

	let t: any = undefined;

	const updateRecord = async () => {
		if (table === 'course') {
			t = await airtable.courses.update(recordId, update_details);
		} else if (table === 'unit') {
			t = await airtable.units.update(recordId, update_details);
		} else if (table === 'week') {
			t = await airtable.weeks.update(recordId, update_details);
		} else if (table === 'resource') {
			t = await airtable.resources.update(recordId, update_details);
		}

		console.log('Record updated:', t);
	};

	updateRecord();
  }, []);

	return (
		<main className="courses-page page">
			<div className="max-w-7xl mx-auto my-12">
				<h1 className="text-3xl font-bold mb-6">Courses</h1>
				<p>Welcome to the Courses page. Here you will find a list of all available courses.</p>
				{/* Add more content or components related to courses here */}
				{courses.map((course, i) => (
					<AccordionItem key={course.id} title={course[airtableFields.courses.course]}>
						<div>
							{course[airtableFields.courses.description] && (
								<p>
									{course[airtableFields.courses.description]}
								</p>
							)}
							{course[airtableFields.courses.overview] && (
								<p>
									<a 
									href={course[airtableFields.courses.overview][0]['url']}
										rel="noopener noreferrer" 
										className='App-link' 
									onClick={() => user?.id && course.id && clickHandler('course', airtableFields.courses.downloadedBy, course.id, course[airtableFields.courses.downloadedBy], user.id)} >
										View Course Overview Document
									</a>
								</p>
							)}
						</div>
						{course.id && getUnitsForCourse(course.id).map(unit => (
							<AccordionItem key={unit.id} title={unit[airtableFields.units.unit]}>
								<div>
									{unit[airtableFields.units.description] && (
										<p>
											{unit[airtableFields.units.description]}
										</p>
									)}
									{unit[airtableFields.units.overview] && (
										<p>
											<a 
											href={unit[airtableFields.units.overview][0]['url']}
												rel="noopener noreferrer" 
												className='App-link' 
											onClick={() => user?.id && unit.id && clickHandler('unit', airtableFields.units.downloadedBy, unit.id, unit[airtableFields.units.downloadedBy], user.id)}>
												View Unit Overview Document
											</a>
										</p>
									)}
								</div>
								{unit.id && getWeeksForUnit(unit.id).map(week => (
									<AccordionItem key={week.id} title={week[airtableFields.weeks.week]}>
										<div>
											{week[airtableFields.weeks.powerpoint] && (
												<p>
													<a 
													href={week[airtableFields.weeks.powerpoint][0]['url']}
														rel="noopener noreferrer" 
														className='App-link' 
													onClick={() => user?.id && week.id && clickHandler('week', airtableFields.weeks.downloadedBy, week.id, week[airtableFields.weeks.downloadedBy], user.id)}>
														View Weekly Powerpoint
													</a>
												</p>
											)}
										</div>
										{week.id && getLessonsForWeek(week.id).map(lesson => (
											<AccordionItem key={lesson.id} title={lesson[airtableFields.lessons.lesson]}>
												 <ul>
													{lesson.id && getResourcesForLesson(lesson.id).map(resource => (
														<li key={resource.id}>
															<a 
														href={resource[airtableFields.resources.resource] && resource[airtableFields.resources.resource][0]['url']}
																rel="noopener noreferrer" 
														className={resource[airtableFields.resources.resource] && 'App-link'}
														onClick={() => user?.id && resource.id && clickHandler('resource', airtableFields.resources.downloadedBy, resource.id, resource[airtableFields.resources.downloadedBy], user.id)}>
														{resource[airtableFields.resources.name]}
															</a>
														</li>
													))}
												</ul>
											</AccordionItem>
										))}
									</AccordionItem>
								))}
							</AccordionItem>
						))}
					</AccordionItem>
				))}
			</div>
		</main>
	);
}
